import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  KanbanSquare,
  Inbox,
  AlertTriangle,
  BarChart3,
  Users,
  Bell,
  LogOut,
  Menu,
  X,
  Factory,
  Plus,
  Plug,
  MessageCircle,
  FileText,
  FileSpreadsheet,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AddLeadDialog from "@/components/AddLeadDialog";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/pipeline", label: "Pipeline", icon: KanbanSquare, testid: "nav-pipeline" },
  { to: "/leads", label: "All Leads", icon: Inbox, testid: "nav-leads" },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle, testid: "nav-whatsapp" },
  { to: "/proforma", label: "Proforma Invoice", icon: FileText, testid: "nav-proforma" },
  { to: "/quotation", label: "Quotation", icon: FileSpreadsheet, testid: "nav-quotation" },
  { to: "/needs-action", label: "Needs Action", icon: AlertTriangle, testid: "nav-needs-action" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/integrations", label: "Lead Capture", icon: Plug, testid: "nav-integrations", roles: ["owner", "manager"] },
  { to: "/team", label: "Team", icon: Users, testid: "nav-team", roles: ["owner", "manager"] },
];

function SidebarContent({ user, onNavigate }) {
  return (
    <div className="flex h-full flex-col">
      <Link to="/" onClick={onNavigate} className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-700/50">
        <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center">
          <Factory className="h-5 w-5 text-white" />
        </div>
        <div className="leading-tight">
          <p className="text-white font-display font-semibold text-sm">Lami Tube</p>
          <p className="text-slate-400 text-[11px]">Lead Funnel</p>
        </div>
      </Link>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {NAV.filter((n) => !n.roles || n.roles.includes(user?.role)).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={onNavigate}
            data-testid={n.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
              }`
            }
          >
            <n.icon className="h-4 w-4" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-slate-700/50">
        <p className="text-slate-200 text-sm font-medium truncate">{user?.name}</p>
        <p className="text-slate-500 text-xs">{user?.role === "owner" ? "Admin" : (user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : "")}</p>
      </div>
    </div>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [addOpen, setAddOpen] = useState(false);

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
    } catch {}
  };

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, []);

  const unread = notifs.filter((n) => !n.read).length;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    loadNotifs();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-slate-900 z-30">
        <SidebarContent user={user} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-slate-900">
            <button
              className="absolute top-4 right-4 text-slate-400"
              onClick={() => setMobileOpen(false)}
              data-testid="close-mobile-menu"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent user={user} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-20 h-16 bg-white/90 backdrop-blur-md border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6">
          <button
            className="lg:hidden text-slate-700"
            onClick={() => setMobileOpen(true)}
            data-testid="open-mobile-menu"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1" />
          <Button
            onClick={() => setAddOpen(true)}
            data-testid="topbar-add-lead"
            className="bg-blue-600 hover:bg-blue-700 text-white h-9"
          >
            <Plus className="h-4 w-4 mr-1.5" /> <span className="hidden sm:inline">New Lead</span>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="relative p-2 rounded-md hover:bg-slate-100" data-testid="notifications-bell">
                <Bell className="h-5 w-5 text-slate-700" />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {unread}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="flex items-center justify-between px-2 py-1.5">
                <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
                {unread > 0 && (
                  <button onClick={markAll} className="text-xs text-blue-600 hover:underline" data-testid="mark-all-read">
                    Mark all read
                  </button>
                )}
              </div>
              <DropdownMenuSeparator />
              <div className="max-h-80 overflow-y-auto">
                {notifs.length === 0 && (
                  <p className="px-3 py-6 text-center text-sm text-slate-400">No notifications</p>
                )}
                {notifs.slice(0, 30).map((n) => (
                  <button
                    key={n.id}
                    onClick={() => n.lead_id && navigate(`/leads/${n.lead_id}`)}
                    className={`w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 ${
                      !n.read ? "bg-blue-50/50" : ""
                    }`}
                  >
                    <p className="text-sm text-slate-800 font-medium">{n.reason}</p>
                    <p className="text-xs text-slate-500 truncate">{n.lead_company}</p>
                  </button>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2" data-testid="user-menu">
                <div className="h-8 w-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-xs font-semibold">
                  {(user?.name || "U").slice(0, 2).toUpperCase()}
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>
                <p className="font-medium">{user?.name}</p>
                <p className="text-xs text-slate-500 font-normal">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async () => {
                  await logout();
                  navigate("/login");
                }}
                data-testid="logout-btn"
                className="text-red-600"
              >
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
      </div>

      <AddLeadDialog open={addOpen} onOpenChange={setAddOpen} onCreated={loadNotifs} />
    </div>
  );
}
